package music.jammers.client.shared.event.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import music.jammers.client.shared.analytics.event.EventAnalytics
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.client.shared.event.presentation.mapper.EventListItemMapper
import music.jammers.client.shared.event.presentation.model.EventListItem
import music.jammers.shared.event.domain.model.Event
import music.limasoul.client.shared.event.domain.usecase.LoadEventsUseCase
import music.limasoul.client.shared.navigation.Navigation
import music.limasoul.client.shared.navigation.Router
import music.limasoul.client.shared.user.domain.usecase.GetCurrentUserUseCase

class EventListViewModel(
    private val loadEventsUseCase: LoadEventsUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val eventAnalytics: EventAnalytics,
    private val eventListItemMapper: EventListItemMapper,
    private val router: Router,
) : BaseViewModel() {

    private val _state = mutableStateOf(EventListState())
    internal val state: State<EventListState> get() = _state

    private val events = mutableStateOf<List<Event>>(emptyList())

    private val errorHandler = customExceptionHandler(router) { _, throwable ->
        eventAnalytics.onError(throwable)

        _state.update {
            copy(
                progress = false,
                error = throwable,
            )
        }
    }

    internal fun onReady() {
        _state.update {
            copy(
                progress = true
            )
        }

        launch(errorHandler) {
            events.value = loadEventsUseCase()

            val user = getCurrentUserUseCase()

            _state.update {
                copy(
                    progress = false,
                    eventList = events.value.map(eventListItemMapper::from),
                    isAdmin = user.isAdmin,
                )
            }
        }
    }

    internal fun onAddEventClicked() {
        router.navigateTo(Navigation.Event.Create)
    }

    internal fun onErrorShown() {
        _state.update {
            copy(
                progress = false,
                error = null,
            )
        }
    }

    internal fun onCardClicked(event: EventListItem) {
        eventAnalytics.onEventClicked(event.id)
        router.navigateTo(Navigation.Event.Details(event.id))
    }
}

data class EventListState(
    val progress: Boolean = false,
    val eventList: List<EventListItem> = emptyList(),
    val isAdmin: Boolean = false,
    val error: Throwable? = null,
)